import { useContext, type ReactNode } from 'react';
import {
  type Key,
  ListBox,
  ListBoxItem,
  type Selection,
} from 'react-aria-components';
import {
  DietaryRestrictions,
  type NonNullableQuiz,
  type Quiz,
  type TagId,
} from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { type QuizTypes } from '../../quiz-hooks';

import { type QuizPreferenceItem } from '../../segment/quizAnalytics';
import { toPascalCase } from '../../utils/toPascalCase';
import { type OnItemSelection } from '../control-utils';
import styles from './ControlSelect.module.css';

interface Props<T extends keyof Quiz> {
  testid?: string;
  ariaLabel: string;
  items?: { label: string; value: QuizTypes }[] | undefined;
  selectedKeys?: QuizTypes[];
  max?: number;
  question: T;
  children?: ReactNode;
  layoutVariant?:
    | 'one-column'
    | 'two-column'
    | 'three-column'
    | 'two-column-desktop'
    | 'one-column-to-two-column';
  defaultValue?: Quiz[T] | NonNullableQuiz[T] | TagId[];
  onItemSelectionChange?: OnItemSelection[];
  onSelectionChange?: (keys: Quiz[T]) => void;
  onTrackSelected?: (items: QuizPreferenceItem[]) => void;
}

function valueIsArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

const getValue = (isArray: boolean, arr: Key[]) => {
  if (isArray) {
    return arr;
  }
  if (arr.length > 0) {
    return arr[0];
  }
  return null;
};

/**
 * For persistence and active states, see `use-quiz-loader.ts`
 */
export function ControlSelect<T extends keyof Quiz>(props: Props<T>) {
  const {
    ariaLabel,
    items,
    selectedKeys,
    max,
    children,
    question,
    defaultValue,
    layoutVariant,
    onItemSelectionChange,
    onTrackSelected,
  } = props;
  const selected = selectedKeys as Key[];

  const { quiz, onQuestionAnswer } = useContext(QuizContext);

  const quizValue = quiz[question];
  const normalized =
    (quizValue === null && defaultValue !== undefined) ||
    (quizValue === undefined && defaultValue !== undefined)
      ? defaultValue
      : quizValue;
  const isArray = Array.isArray(normalized);
  return (
    <ListBox
      aria-label={ariaLabel}
      items={items}
      selectedKeys={selected}
      onSelectionChange={(keys: Selection) => {
        const arr = Array.from(keys);
        const value = getValue(isArray, arr) as Exclude<
          (typeof quiz)[typeof question],
          null
        >;

        const valuesAreArrays = valueIsArray(value) || valueIsArray(quizValue);

        if (onItemSelectionChange && valuesAreArrays) {
          const didChange = quizValue !== value;
          onItemSelectionChange.forEach((item) => {
            const valuesContainItem =
              ((quizValue as QuizTypes[]) ?? []).includes(item.itemValue) ||
              ((value as QuizTypes[]) ?? [])?.includes(item.itemValue);
            const itemValueChanged =
              ((quizValue as QuizTypes[]) ?? []).includes(item.itemValue) !==
              ((value as QuizTypes[]) ?? []).includes(item.itemValue);
            if (didChange && valuesContainItem && itemValueChanged) {
              item.handler();
            }
          });
        }

        if (isArray === false && value === quizValue) {
          onQuestionAnswer(quiz, question, null);
          return;
        }

        if (valueIsArray(value) && max !== undefined && value.length > max) {
          // We've selected more than allowed, don't persist
          return;
        }

        if (onTrackSelected !== undefined) {
          const trackedItems =
            valueIsArray(value) && items !== undefined
              ? items.reduce((acc, item) => {
                  if ((value as QuizTypes[]).includes(item.value)) {
                    if (question === 'dietary') {
                      const key = toPascalCase(item.value as string);
                      acc.push({
                        name: item.label,
                        id: DietaryRestrictions[
                          key as keyof typeof DietaryRestrictions
                        ],
                        type: 'tag' as const,
                      });
                    } else {
                      acc.push({
                        name: item.label,
                        id: Number(item.value),
                        type: 'tag' as const,
                      });
                    }
                  }
                  return acc;
                }, [] as QuizPreferenceItem[])
              : [];

          onTrackSelected(trackedItems);
        }

        onQuestionAnswer(quiz, question, value);
      }}
      selectionMode={isArray ? 'multiple' : 'single'}
      className={`${styles.options} ${
        layoutVariant ? styles[layoutVariant] : ''
      }`}
    >
      {children}
    </ListBox>
  );
}

ControlSelect.Option = (props: {
  children: ReactNode;
  id: QuizTypes;
  label: string;
}) => {
  const { children, label, id } = props;

  return (
    <ListBoxItem
      className={({ isFocusVisible }) =>
        `${isFocusVisible ? styles.focus : styles.option}`
      }
      id={id as Key}
      textValue={label}
    >
      {children}
    </ListBoxItem>
  );
};
